/**
 * Discharge Validation Utilities
 *
 * The backend POST /validate endpoint is the source of truth for validation.
 * These client-side helpers are used for:
 * (a) categorizing the API's validations[] response into UI groups
 * (b) mapping backend field names to UI-friendly names
 * (c) optional client-side pre-validation for quick UX hints
 */

import {
  BLOCKED_STATUSES,
  DISCHARGE_LIMITS,
  DISCHARGE_MESSAGES,
  NO_FORFEITURE_DISCHARGE_STATES,
  SPECIAL_LIMIT_STATES,
  buildStateForfeitureOverLimitMessage,
  buildStateOverLimitMessage,
} from './constants';

/**
 * Categorize the backend validation response into UI groups.
 * This is the primary function — maps API response to UI sections.
 * @param {Array} validations - Array of validation items from POST /validate
 * @returns {{ ready: Array, needsProof: Array, invalid: Array }}
 */
export const categorizeValidations = (validations) => ({
  ready: validations.filter((v) => v.isValid && !v.requiresProof),
  needsProof: validations.filter((v) => v.isValid && v.requiresProof),
  invalid: validations.filter((v) => !v.isValid),
});

/**
 * Build display-friendly objects from API validation items.
 * Maps backend field names to UI-expected names:
 *   bondAmount → liability, defendantName → defendant, proofReason → reason
 * @param {Object} validation - Single validation item from API
 * @returns {Object} Display-friendly object
 */
export const mapValidationToDisplay = (validation) => ({
  powerNo: validation.powerNo,
  defendant: validation.defendantName,
  liability: validation.bondAmount,
  status: validation.powerStatus,
  state: validation.state,
  requiresProof: validation.requiresProof,
  reason: validation.proofReason,
  validationErrors: validation.validationErrors,
  isValid: validation.isValid,
});

/**
 * Client-side pre-validation (optional, for quick UX hints before API call).
 * Checks in order (matching backend DischargeController):
 * 1. Already discharged → blocked
 * 2. Unexecuted → blocked
 * 3. Open batch → blocked
 * 4. Judgment without proof → needsProof
 * 5. Transfer bond → same validation as other bonds (amount limits, state rules)
 * 6. State-specific forfeiture limits → needsProof or blocked (NC/TX → blocked)
 * 7. Normal amount limits by state → needsProof
 * 8. Otherwise → ready
 *
 * @param {Object} power - Power object with status, state, liability, etc.
 * @returns {{ status: 'ready'|'needsProof'|'blocked', reason?: string }}
 */
export const validatePowerForDischarge = (power) => {
  const { powerStatus, state, liability, batchClosed, forfeitureStatus } = power;

  // Raised thresholds for CA/CT, undefined for every other state.
  const specialLimits = SPECIAL_LIMIT_STATES[state];

  // 1. Already discharged
  if (powerStatus === BLOCKED_STATUSES.DISCHARGED) {
    return { status: 'blocked', reason: DISCHARGE_MESSAGES.ALREADY_DISCHARGED };
  }

  // 2. Unexecuted
  if (powerStatus === BLOCKED_STATUSES.UNEXECUTED) {
    return { status: 'blocked', reason: DISCHARGE_MESSAGES.UNEXECUTED };
  }

  // 3. Open batch
  if (batchClosed === false) {
    return { status: 'blocked', reason: DISCHARGE_MESSAGES.OPEN_BATCH };
  }

  // 4. Judgment
  if (powerStatus === 'J') {
    return { status: 'needsProof', reason: DISCHARGE_MESSAGES.JUDGMENT };
  }

  // 5. Transfer bond — same validation rules as other bonds (amount limits, state rules)
  // Falls through to forfeiture checks (if applicable) and normal amount limits below.

  // 6. Forfeiture rules
  if (forfeitureStatus === 'F' || powerStatus === 'F') {
    if (NO_FORFEITURE_DISCHARGE_STATES.includes(state)) {
      return { status: 'needsProof', reason: DISCHARGE_MESSAGES.NC_TX_FORFEITURE };
    }
    const forfeitureLimit = specialLimits
      ? specialLimits.forfeiture
      : DISCHARGE_LIMITS.FORFEITURE_DEFAULT;
    if (liability > forfeitureLimit) {
      return {
        status: 'needsProof',
        reason: specialLimits
          ? buildStateForfeitureOverLimitMessage(state, forfeitureLimit)
          : DISCHARGE_MESSAGES.FORFEITURE_OVER_LIMIT,
      };
    }
  }

  // 7. Normal amount limits
  const normalLimit = specialLimits ? specialLimits.normal : DISCHARGE_LIMITS.DEFAULT;
  if (liability > normalLimit) {
    return {
      status: 'needsProof',
      reason: specialLimits
        ? buildStateOverLimitMessage(state, normalLimit)
        : DISCHARGE_MESSAGES.OVER_LIMIT,
    };
  }

  // 8. Ready
  return { status: 'ready' };
};
